'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import {
  CaretLeft, CaretDown, Users, Phone, MapPin,
} from '@phosphor-icons/react';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import { VACANCIES, PERKS, CONTACT } from './vacancies-data';
import type { Vacancy } from './vacancies-data';

function MaxIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="-100 -100 960 958" fill="currentColor" className={className}>
      <path fillRule="evenodd" clipRule="evenodd" d="M392.07 186.804C292.648 181.591 215.063 250.579 197.954 358.557C183.786 447.977 208.906 556.935 230.392 562.419C239.505 564.745 261.436 547.971 277.392 532.925C280.391 530.096 284.927 529.62 288.445 531.771C313.317 546.98 341.477 558.41 372.522 560.038C474.59 565.39 565.042 485.507 570.388 383.43C575.735 281.353 494.139 192.157 392.07 186.804ZM225.581 705.191C221.732 702.466 216.428 703.205 213.202 706.646C170.082 752.636 59.7277 784.896 54.6727 722.126C54.6727 672.946 43.6266 631.503 31.4646 585.873C16.5692 529.989 0 467.824 0 377.475C0 162.027 176.694 0 386.194 0C595.694 0 759.951 169.938 759.951 379.567C759.951 589.196 590.466 754.95 388.176 754.95C316.409 754.95 281.583 744.841 225.581 705.191Z" />
    </svg>
  );
}

function VacancyAvatar({ src, alt }: { src: string; alt: string }) {
  return (
    <div className="relative w-[72px] h-[82px] shrink-0">
      {/* Background circle */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[60px] h-[60px] rounded-pill bg-[var(--primary-subtle)]" />

      {/* Image with simple rounded clip — compatible with all browsers */}
      <div
        className="absolute inset-x-0 bottom-0 overflow-hidden mx-auto"
        style={{
          width: 64,
          height: 64,
          borderRadius: '0 0 50% 50% / 0 0 40% 40%',
        }}
      >
        <Image
          src={src}
          alt={alt}
          width={100}
          height={100}
          className="w-full h-auto object-contain"
          unoptimized
        />
      </div>
    </div>
  );
}

function VacancyCard({ vacancy, index }: { vacancy: Vacancy; index: number }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="animate-fade-up" style={{ animationDelay: `${index * 80}ms` }}>
      <div
        className={cn(
          'rounded-card border transition-all duration-300',
          'bg-[var(--surface-primary)] border-[var(--border-default)]',
          // shadow-[var(--primary)]/5 здесь был мёртвым: Tailwind v4 разбирает
          // shadow-[var(--…)] как ЗНАЧЕНИЕ тени, а не как её цвет, и соседний
          // shadow-lg объявлен в CSS позже — то есть все эти годы рисовалась
          // обычная чёрная тень, а не фиолетовая. Класс удалён (ответ 35: чинить
          // «по-настоящему» = вариант В, отклонён). Глубина уплощена до float.
          'hover:border-[var(--border-hover)] hover:shadow-float',
          open && 'border-[var(--border-hover)] shadow-float',
        )}
      >
        <button
          onClick={() => setOpen(!open)}
          className="w-full text-left p-5 sm:p-6 flex items-center gap-4"
        >
          <VacancyAvatar src={vacancy.image} alt={vacancy.title} />
          <div className="flex-1 min-w-0">
            <h3 className="t-card-title font-bold text-[var(--text-primary)]">
              {vacancy.title}
            </h3>
            <span className="text-xs font-semibold text-[var(--primary)] bg-[var(--primary-subtle)] px-3 py-1 rounded-pill font-numbers mt-2 inline-block">
              {vacancy.salary}
            </span>

          </div>
          <CaretDown
            weight="bold"
            className={cn(
              'w-5 h-5 text-[var(--text-tertiary)] transition-transform duration-300 shrink-0',
              open && 'rotate-180 text-[var(--primary)]',
            )}
          />
        </button>

        {open && (
          <div className="px-5 sm:px-6 pb-5 sm:pb-6 border-t border-[var(--border-default)]">
            <div className="flex flex-wrap gap-2 pt-5 mb-4">
              <span className="text-xs font-medium px-3 py-1.5 rounded-card bg-[var(--surface-tertiary)] text-[var(--text-secondary)]">
                {vacancy.schedule}
              </span>
              <span className="text-xs font-medium px-3 py-1.5 rounded-card bg-[var(--surface-tertiary)] text-[var(--text-secondary)]">
                {vacancy.type}
              </span>
            </div>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary)] mb-3">
                  Обязанности
                </p>
                <ul className="space-y-2">
                  {vacancy.duties.map((d, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-[var(--text-secondary)]">
                      <span className="mt-2 w-1.5 h-1.5 rounded-pill bg-[var(--primary)] shrink-0" />
                      {d}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary)] mb-3">
                  Требования
                </p>
                <ul className="space-y-2">
                  {vacancy.requirements.map((r, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-[var(--text-secondary)]">
                      <span className="mt-2 w-1.5 h-1.5 rounded-pill bg-[var(--violet-400)] shrink-0" />
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="mt-5 pt-5 border-t border-[var(--border-default)] flex flex-wrap items-center gap-3">
              <a
                href={CONTACT.max.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-card bg-[var(--primary)] !text-white text-sm font-semibold transition-all duration-200 hover:bg-[var(--primary-hover)] hover:shadow-float"
              >
                <MaxIcon className="w-4 h-4" />
                {CONTACT.max.label}
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function VacanciesContent() {
  const router = useRouter();

  return (
    <div className="min-h-screen bg-[var(--bg-primary)] overflow-x-hidden">
      {/* Header */}
      <div
        className="sticky top-0 z-30 border-b backdrop-blur-md bg-[var(--surface-primary)]/95 border-[var(--border-default)]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-12 sm:h-14 flex items-center gap-3">
          <button
            onClick={() => router.back()}
            className="p-1.5 -ml-1.5 rounded-card hover:bg-[var(--surface-hover)] transition-colors shrink-0"
          >
            <CaretLeft className="w-4 h-4 text-[var(--text-secondary)]" weight="bold" />
          </button>
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-6 h-6 rounded-card flex items-center justify-center shrink-0 bg-[var(--primary-subtle)]">
              <Users className="w-3.5 h-3.5 text-[var(--primary)]" weight="fill" />
            </div>
            <h1 className="text-sm sm:text-base font-semibold text-[var(--text-primary)] truncate">
              Вакансии
            </h1>
          </div>
        </div>
      </div>

      {/* Breadcrumbs */}
      <nav aria-label="Хлебные крошки" className="max-w-7xl mx-auto px-4 sm:px-6 pt-4 pb-1">
        <ol className="flex flex-wrap items-center gap-1.5 text-xs sm:text-sm">
          <li className="flex items-center gap-1.5">
            <Link href="/" className="text-[var(--text-secondary)] hover:text-[var(--primary)] transition-colors duration-150 whitespace-nowrap">Главная</Link>
          </li>
          <li className="flex items-center gap-1.5">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-[var(--text-tertiary)] shrink-0" aria-hidden="true">
              <path d="M6 3.5L10.5 8L6 12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="text-[var(--text-tertiary)]">Вакансии</span>
          </li>
        </ol>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10 space-y-10 sm:space-y-14">
        {/* Intro */}
        <section className="max-w-3xl animate-fade-up">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-pill bg-[var(--primary-subtle)] text-[var(--primary)] text-sm font-semibold mb-5">
            <Users weight="fill" className="w-4 h-4" />
            Карьера в Азиаточка
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-black tracking-tight text-[var(--text-primary)] mb-4">
            Присоединяйся к{' '}
            <span className="text-[var(--primary)]">команде</span>
          </h2>
          <p className="text-base text-[var(--text-secondary)] leading-relaxed max-w-2xl">
            Мы растём и ищем людей, которые разделяют нашу
            любовь к качественной еде и отличному сервису.
          </p>
        </section>

        {/* Positions */}
        <section>
          <div className="flex items-center justify-between mb-6 animate-fade-up">
            <h3 className="text-xl sm:text-2xl font-heading font-bold text-[var(--text-primary)]">
              Открытые позиции
            </h3>
            <span className="text-sm font-numbers font-semibold text-[var(--text-tertiary)] bg-[var(--surface-tertiary)] px-3 py-1 rounded-pill">
              {VACANCIES.length} вакансий
            </span>
          </div>
          <div className="space-y-3">
            {VACANCIES.map((v, i) => (
              <VacancyCard key={v.id} vacancy={v} index={i} />
            ))}
          </div>
        </section>

        {/* Perks */}
        <section className="relative">
          <div className="blur-blob absolute -top-20 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-pill bg-[var(--primary)]/6 blur-[100px] pointer-events-none" />
          <div className="relative z-10">
            <div className="text-center mb-10 animate-fade-up">
              <h3 className="text-2xl sm:text-3xl font-heading font-bold text-[var(--text-primary)] mb-2">
                Что мы предлагаем
              </h3>
              <p className="text-[var(--text-secondary)]">
                Работа, которая приносит удовольствие
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {PERKS.map((perk, i) => {
                const PerkIcon = perk.icon;
                return (
                  <div
                    key={i}
                    className={cn(
                      'p-5 rounded-card border transition-all duration-300 animate-fade-up',
                      'bg-[var(--surface-primary)] border-[var(--border-default)]',
                      'hover:border-[var(--border-hover)] hover:shadow-float hover:-translate-y-0.5',
                    )}
                    style={{ animationDelay: `${i * 60}ms` }}
                  >
                    <div className="w-10 h-10 rounded-card bg-[var(--primary-subtle)] flex items-center justify-center mb-3">
                      <PerkIcon weight="fill" className="w-5 h-5 text-[var(--primary)]" />
                    </div>
                    <h4 className="text-sm font-bold text-[var(--text-primary)] mb-1">
                      {perk.title}
                    </h4>
                    <p className="text-xs text-[var(--text-tertiary)] leading-relaxed">
                      {perk.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section
          className={cn(
            'rounded-card border overflow-hidden animate-fade-up',
          )}
          style={{
            background: 'linear-gradient(to bottom right, #7B42FF, #5B28D1)',
            borderColor: 'rgba(123, 66, 255, 0.20)',
          }}
        >
          <div className="p-8 sm:p-12 text-center">
            <h3 className="text-2xl sm:text-3xl font-heading font-black text-[var(--text-on-primary)] mb-3">
              Готов начать?
            </h3>
            <p className="text-[var(--text-on-primary)] mb-8 max-w-lg mx-auto" style={{ opacity: 0.7 }}>
              Мы рассматриваем кандидатов без опыта работы. Главное — желание
              учиться и работать в команде. Напиши нам — расскажем подробнее
              и пригласим на собеседование.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href={CONTACT.max.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-card bg-[var(--surface-primary)] text-[var(--primary)] text-sm font-bold transition-all duration-200 hover:shadow-float hover:-translate-y-0.5"
              >
                <MaxIcon className="w-5 h-5" />
                {CONTACT.max.label}
              </a>
            </div>
            <div className="flex items-center justify-center gap-2 mt-6 text-[var(--text-on-primary)] text-xs" style={{ opacity: 0.5 }}>
              <MapPin weight="fill" className="w-3.5 h-3.5" />
              {CONTACT.city}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
